import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { ArrowLeft, Upload, Trash2, X, FileText, XCircle } from "lucide-react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { compressImage } from "../utils/imageCompression";
import { useHotel } from "../context/HotelContext";
import { uploadHotelDocument, deleteHotelDocument } from "../services/api";
import LoadingGif from "../components/LoadingGif";
import { convertFromISO } from "../utils/dateValidation";

const HotelDocumentPage = () => {
  const navigate = useNavigate();
  const { hotelId } = useParams();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { fetchDraft, loading, fetchDocuments, } = useHotel();
  const [businessRegFile, setBusinessRegFile] = useState(null);
  const [businessRegFileDate, setBusinessRegFileDate] = useState(null);
  const [animalLicenseFile, setAnimalLicenseFile] = useState(null);
  const [animalLicenseFileDate, setAnimalLicenseFileDate] = useState(null);
  const [businessRegPreviewUrl, setBusinessRegPreviewUrl] = useState(null);
  const [animalLicensePreviewUrl, setAnimalLicensePreviewUrl] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [businessRegError, setBusinessRegError] = useState(false);
  const [animalLicenseError, setAnimalLicenseError] = useState(false);

  const [isBusinessRegUploading, setIsBusinessRegUploading] = useState(false);
  const [isAnimalLicenseUploading, setIsAnimalLicenseUploading] = useState(false);
  const [businessRegUploaded, setBusinessRegUploaded] = useState(false);
  const [animalLicenseUploaded, setAnimalLicenseUploaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const businessRegInputRef = useRef(null);
  const animalLicenseInputRef = useRef(null);
  const [uploadedDocuments, setUploadedDocuments] = useState(null);
  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

  const [isBookingTermsUploading, setIsBookingTermsUploading] = useState(false);
  const [bookingTermsUploaded, setBookingTermsUploaded] = useState(false);
  const [requireBookingTerms, setRequireBookingTerms] = useState("");
  const [bookingTermsFile, setBookingTermsFile] = useState(null);
  const [bookingTermsPreviewUrl, setBookingTermsPreviewUrl] = useState(null);
  const [bookingTermsError, setBookingTermsError] = useState(false);
  const bookingTermsInputRef = useRef(null);
  const [bookingTermsFileDate, setBookingTermsFileDate] = useState(null);


  useEffect(() => {
    if (hotelId) {
      fetchDraft(hotelId);
    }
  }, [hotelId, fetchDraft]);

  useEffect(() => {
    if (hotelId) {
      fetchDocuments().then((response) => {
        const documents = response.data?.data || [];
        const businessReg = documents.find((doc) => doc.type === "businessRegFile");
        const animalLicense = documents.find((doc) => doc.type === "animalLicenseFile");
        const bookingTerms = documents.find((doc) => doc.type === "bookingTermsFile");
        setUploadedDocuments({ businessReg, animalLicense, bookingTerms });

        if (businessReg) {
          setBusinessRegUploaded(true);
          setBusinessRegFile({ name: businessReg.fileName || "Business Registration Certificate.pdf" });
          const fileUrl = businessReg.fileUrl || businessReg.url || businessReg.link;
          setBusinessRegPreviewUrl(fileUrl);
          setBusinessRegFileDate(businessReg.uploadedAt);
        }

        if (animalLicense) {
          setAnimalLicenseUploaded(true);
          setAnimalLicenseFile({ name: animalLicense.fileName || "Animal Boarding Licence.pdf" });
          const fileUrl = animalLicense.fileUrl || animalLicense.url || animalLicense.link;
          setAnimalLicensePreviewUrl(fileUrl);
          setAnimalLicenseFileDate(animalLicense.uploadedAt);
        }

        if (bookingTerms) {
          setBookingTermsUploaded(true);
          setBookingTermsFile({ name: bookingTerms.fileName || "Booking Terms.pdf" });
          const fileUrl = bookingTerms.fileUrl || bookingTerms.url || bookingTerms.link;
          setBookingTermsPreviewUrl(fileUrl);
          setBookingTermsFileDate(bookingTerms.uploadedAt);
        }
      }).catch((err) => console.error("Failed to fetch documents:", err));
    }
  }, [hotelId, fetchDocuments]);

  const handleFileSelect = async (file, setFile, setError) => {
    if (!file) {
      setError(false);
      setFile(null);
      return;
    }

    const processedFile = file.type.startsWith("image/") ? await compressImage(file) : file;

    if (processedFile.size > MAX_FILE_SIZE_BYTES) {
      setFile(null);
      setError(true);
      return;
    }
    setError(false);
    setFile(processedFile);
  };

  useEffect(() => {
    if (!businessRegFile) {
      setBusinessRegPreviewUrl(null);
      return;
    }
    if (businessRegFile instanceof File) {
      const url = URL.createObjectURL(businessRegFile);
      setBusinessRegPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [businessRegFile]);

  useEffect(() => {
    if (!animalLicenseFile) {
      setAnimalLicensePreviewUrl(null);
      return;
    }
    if (animalLicenseFile instanceof File) {
      const url = URL.createObjectURL(animalLicenseFile);
      setAnimalLicensePreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [animalLicenseFile]);
  const [formData, setFormData] = useState({
    legalRepresentative: "",
    businessBranchName: "",
    registrationCertNumber: "",
    address: "",
    operationPeriod: "",
    businessNature: "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileUpload = async (file, setUploading, setUploaded, docType) => {
    if (!file || !hotelId) return;
    try {
      setUploading(true);
      const response = await uploadHotelDocument(hotelId, file, docType);
      if (response.data?.success || response.status === 201) {
        setUploaded(true);
      } else {
        console.error(`Upload failed for ${docType}`);
      }
    } catch (err) {
      console.error(`Failed to upload ${docType}:`, err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!documentId || !hotelId) return;
    try {
      await deleteHotelDocument(hotelId, documentId);
      setBusinessRegFile(null);
      setBusinessRegPreviewUrl(null);
      setAnimalLicenseFile(null);
      setAnimalLicensePreviewUrl(null);
      setBusinessRegUploaded(false);
      setAnimalLicenseUploaded(false);
      setUploadedDocuments(null);
    } catch (err) {
      console.error("Failed to delete document:", err);
    }
  };

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  const uploadBoxStyle = {
    display: "flex",
    alignItems: "center",
    gap: 2,
    backgroundColor: "#FFFFFF",
    border: "1.5px solid #D1D1D1",
    borderRadius: "10px",
    padding: isMobile ? "16px" : "20px 24px",
    cursor: "pointer",
    width: "100%",
    boxSizing: "border-box",
  };

  const fileCardStyle = {
    display: "flex",
    alignItems: "center",
    gap: 2,
    backgroundColor: "#FFFFFF",
    border: "1.5px solid #D1D1D1",
    borderRadius: "10px",
    padding: isMobile ? "16px" : "20px 24px",
    width: "80%",
    boxSizing: "border-box",
    cursor: "pointer",
  };

  const isPdf = (file) => file && (file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf"));

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Button
              onClick={() => navigate(-1)}
              sx={{
                minWidth: "auto",
                width: isMobile ? "36px" : "44px",
                height: isMobile ? "36px" : "44px",
                borderRadius: "50%",
                backgroundColor: "#31BACD",
                color: "#fff",
                padding: 0,
                "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
              }}
            >
              <ArrowLeft size={isMobile ? 18 : 22} />
            </Button>
            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000" }}>
              {`${t('HotelInformation')} > ${t('Documents')}`}
            </Typography>
          </Box>

          {/* Business Registration Certificate */}
          {loading ? (
            <LoadingGif />
          ) : (
            <>
              <Box sx={{ mb: 5 }}>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 0.5 }}>
                  {businessRegUploaded ? t('BusinessRegCertLabel') : t('UploadBusinessRegLabel')}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#4A5565", mb: 2 }}>
                  {businessRegUploaded ? (`${t('UploadDate')}: ${convertFromISO(businessRegFileDate)}`) : t('SupportedFormats')}
                </Typography>
                {!businessRegFile ? (
                  <Box component="label" sx={uploadBoxStyle} >
                    <Box
                      sx={{
                        width: isMobile ? "36px" : "44px",
                        height: isMobile ? "36px" : "44px",
                        borderRadius: "50%",
                        border: "1.5px solid #D1D1D1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Upload size={18} color="#666" />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                        {t('BusinessRegCertLabel')}
                      </Typography>
                      <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#999" }}>
                        {t('BusinessRegCertDesc')}
                      </Typography>
                    </Box>
                    <input
                      ref={businessRegInputRef}
                      type="file"
                      accept=".jpeg,.jpg,.pdf,.png"
                      style={{ display: "none" }}
                      onChange={(e) =>
                        handleFileSelect(e.target.files?.[0] || null, setBusinessRegFile, setBusinessRegError)
                      }
                    />
                  </Box>
                ) : null}
                {businessRegError && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      backgroundColor: "#FFFFFF",
                      border: "1.5px solid #F44336",
                      borderRadius: "10px",
                      padding: isMobile ? "16px" : "20px 24px",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <XCircle size={20} color="#F44336" />
                    <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#F44336" }}>
                      {t('FileTooLargeError')}
                    </Typography>
                  </Box>
                )}
                {businessRegFile && (
                  <Box sx={{ width: "100%", display: "flex", gap: "5%", alignItems: "center", justifyContent: "space-between" }}>
                    <Box
                      sx={fileCardStyle}
                      onClick={() => {
                        setPreviewFile({ file: businessRegFile, url: businessRegPreviewUrl });
                      }}
                    >
                      <Box
                        sx={{
                          width: isMobile ? "40px" : "48px",
                          height: isMobile ? "40px" : "48px",
                          borderRadius: "6px",
                          overflow: "hidden",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#F5F5F5",
                          border: "1px solid #E0E0E0",
                        }}
                      >
                        {isPdf(businessRegFile) ? (
                          <img src="/icons/pdf.svg" alt="PDF" className="h-auto" />
                        ) : (
                          <Box
                            component="img"
                            src={businessRegPreviewUrl}
                            alt={businessRegFile.name}
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        )}
                      </Box>
                      <Typography sx={{ flex: 1, fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                        {businessRegFile.name}
                      </Typography>
                      {!businessRegUploaded && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setBusinessRegFile(null);
                          }}
                          sx={{ minWidth: "auto", padding: "8px", color: "#666", }}
                        >

                          <Trash2 size={18} />
                        </Button>
                      )}
                    </Box>
                    {businessRegUploaded ? (
                      <Button sx={{ padding: "8px 12px", color: "#fff", backgroundColor: "#E5194A", width: "15%", "&:hover": { backgroundColor: "#db6984" }, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, textTransform: "none" }}
                        onClick={() => handleDeleteDocument(uploadedDocuments?.businessReg?.documentId)}>
                        {isBusinessRegUploading ? (<CircularProgress size={18} />) : <Trash2 size={18} />}
                        {isMobile ? null : t('Delete')}
                      </Button>
                    ) : <Button sx={{ padding: "8px 12px", color: "#fff", backgroundColor: "#31BACD", width: "15%", "&:hover": { backgroundColor: "#7dbec6" }, textTransform: "none" }}
                      onClick={() => handleFileUpload(businessRegFile, setIsBusinessRegUploading, setBusinessRegUploaded, "businessRegFile")}>
                      {isBusinessRegUploading ? (<CircularProgress size={18} />) :t('Save')}
                    </Button>}
                  </Box>
                )}
              </Box>

              {/* Animal Boarding Licence */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 0.5 }}>
                  {animalLicenseUploaded ? t('AnimalLicenseLabel') : t('UploadAnimalLicenseLabel')}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#4A5565", mb: 2 }}>
                  {animalLicenseUploaded ? (`${t('UploadDate')}: ${convertFromISO(animalLicenseFileDate)}`) : t('SupportedFormats')}
                </Typography>

                {!animalLicenseFile ? (
                  <Box
                    component="label"
                    sx={uploadBoxStyle}
                  >
                    <Box
                      sx={{
                        width: isMobile ? "36px" : "44px",
                        height: isMobile ? "36px" : "44px",
                        borderRadius: "50%",
                        border: "1.5px solid #D1D1D1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Upload size={18} color="#666" />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                        {t('AnimalLicenseLabel')}
                      </Typography>
                      <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#999" }}>
                        {t('AnimalLicenseDesc')}
                      </Typography>
                    </Box>
                    <input
                      ref={animalLicenseInputRef}
                      type="file"
                      accept=".jpeg,.jpg,.pdf,.png"
                      style={{ display: "none" }}
                      onChange={(e) =>
                        handleFileSelect(e.target.files?.[0] || null, setAnimalLicenseFile, setAnimalLicenseError)
                      }
                    />
                  </Box>
                ) : null}
                {animalLicenseError && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      backgroundColor: "#FFFFFF",
                      border: "1.5px solid #F44336",
                      borderRadius: "10px",
                      padding: isMobile ? "16px" : "20px 24px",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <XCircle size={20} color="#F44336" />
                    <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#F44336" }}>
                      {t('FileTooLargeError')}
                    </Typography>
                  </Box>
                )}
                {animalLicenseFile && (
                  <Box sx={{ width: "100%", display: "flex", gap: "5%", alignItems: "center", justifyContent: "space-between" }}>
                    <Box
                      sx={fileCardStyle}
                      onClick={() => {
                        setPreviewFile({ file: animalLicenseFile, url: animalLicensePreviewUrl });
                      }}
                    >
                      <Box
                        sx={{
                          width: isMobile ? "40px" : "48px",
                          height: isMobile ? "40px" : "48px",
                          borderRadius: "6px",
                          overflow: "hidden",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#F5F5F5",
                          border: "1px solid #E0E0E0",
                        }}
                      >
                        {isPdf(animalLicenseFile) ? (
                          <img src="/icons/pdf.svg" alt="PDF" className="h-auto" />
                        ) : (
                          <Box
                            component="img"
                            src={animalLicensePreviewUrl}
                            alt={animalLicenseFile.name}
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        )}
                      </Box>
                      <Typography sx={{ flex: 1, fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                        {animalLicenseFile.name}
                      </Typography>
                      {!animalLicenseUploaded && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnimalLicenseFile(null);
                          }}
                          sx={{ minWidth: "auto", padding: "8px", color: "#666", }}
                        >
                          <Trash2 size={18} />
                        </Button>
                      )}
                    </Box>
                    {animalLicenseUploaded ? (
                      <Button sx={{ padding: "8px 12px", color: "#fff", backgroundColor: "#E5194A", width: "15%", "&:hover": { backgroundColor: "#db6984" }, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, textTransform: "none" }}
                        onClick={() => handleDeleteDocument(uploadedDocuments?.animalLicense?.documentId)}>
                        {isAnimalLicenseUploading ? (<CircularProgress size={18} />) : <Trash2 size={18} />}
                        {isMobile ? null : t('Delete')}
                      </Button>
                    ) : <Button sx={{ padding: "8px 12px", color: "#fff", backgroundColor: "#31BACD", width: "15%", "&:hover": { backgroundColor: "#7dbec6" }, textTransform: "none" }}
                      onClick={() => handleFileUpload(animalLicenseFile, setIsAnimalLicenseUploading, setAnimalLicenseUploaded, "animalLicenseFile")}>
                      {isAnimalLicenseUploading ? (<CircularProgress size={18} />) : t('Save')}
                    </Button>}
                  </Box>
                )}
              </Box>

              {/* Booking Terms */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 0.5 }}>
                  {bookingTermsUploaded ? t('BookingTermsLabel') : t('UploadBookingTermsLabel')}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#4A5565", mb: 2 }}>
                  {bookingTermsUploaded ? (`${t('UploadDate')}: ${convertFromISO(bookingTermsFileDate)}`) : t('SupportedFormats')}
                </Typography>

                {!bookingTermsFile ? (
                  <Box
                    component="label"
                    sx={uploadBoxStyle}
                  >
                    <Box
                      sx={{
                        width: isMobile ? "36px" : "44px",
                        height: isMobile ? "36px" : "44px",
                        borderRadius: "50%",
                        border: "1.5px solid #D1D1D1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Upload size={18} color="#666" />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                        {t('BookingTermsLabel')}
                      </Typography>
                      <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#999" }}>
                        {t('BookingTermsDesc')}
                      </Typography>
                    </Box>
                    <input
                      ref={bookingTermsInputRef}
                      type="file"
                      accept=".jpeg,.jpg,.pdf,.png"
                      style={{ display: "none" }}
                      onChange={(e) =>
                        handleFileSelect(e.target.files?.[0] || null, setBookingTermsFile, setBookingTermsError)
                      }
                    />
                  </Box>
                ) : null}
                {bookingTermsError && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      backgroundColor: "#FFFFFF",
                      border: "1.5px solid #F44336",
                      borderRadius: "10px",
                      padding: isMobile ? "16px" : "20px 24px",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <XCircle size={20} color="#F44336" />
                    <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#F44336" }}>
                      {t('FileTooLargeError')}
                    </Typography>
                  </Box>
                )}
                {bookingTermsFile && (
                  <Box sx={{ width: "100%", display: "flex", gap: "5%", alignItems: "center", justifyContent: "space-between" }}>
                    <Box
                      sx={fileCardStyle}
                      onClick={() => {
                        setPreviewFile({ file: bookingTermsFile, url: bookingTermsPreviewUrl });
                      }}
                    >
                      <Box
                        sx={{
                          width: isMobile ? "40px" : "48px",
                          height: isMobile ? "40px" : "48px",
                          borderRadius: "6px",
                          overflow: "hidden",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#F5F5F5",
                          border: "1px solid #E0E0E0",
                        }}
                      >
                        {isPdf(bookingTermsFile) ? (
                          <img src="/icons/pdf.svg" alt="PDF" className="h-auto" />
                        ) : (
                          <Box
                            component="img"
                            src={bookingTermsPreviewUrl}
                            alt={bookingTermsFile.name}
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        )}
                      </Box>
                      <Typography sx={{ flex: 1, fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                        {bookingTermsFile.name}
                      </Typography>
                      {!bookingTermsUploaded && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setBookingTermsFile(null);
                          }}
                          sx={{ minWidth: "auto", padding: "8px", color: "#666", }}
                        >
                          <Trash2 size={18} />
                        </Button>
                      )}
                    </Box>
                    {bookingTermsUploaded ? (
                      <Button sx={{ padding: "8px 12px", color: "#fff", backgroundColor: "#E5194A", width: "15%", "&:hover": { backgroundColor: "#db6984" }, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, textTransform: "none" }}
                        onClick={() => handleDeleteDocument(uploadedDocuments?.bookingTerms?.documentId)}>
                        {isBookingTermsUploading ? (<CircularProgress size={18} />) : <Trash2 size={18} />}
                        {isMobile ? null : t('Delete')}
                      </Button>
                    ) : <Button sx={{ padding: "8px 12px", color: "#fff", backgroundColor: "#31BACD", width: "15%", "&:hover": { backgroundColor: "#7dbec6" }, textTransform: "none" }}
                      onClick={() => handleFileUpload(bookingTermsFile, setIsBookingTermsUploading, setBookingTermsUploaded, "bookingTermsFile")}>
                      {isBookingTermsUploading ? (<CircularProgress size={18} />) : t('Save')}
                    </Button>}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </div>
      <Footer />

      {previewFile && (
        <Box
          onClick={() => setPreviewFile(null)}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "20px" : "40px",
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              width: "100%",
              maxWidth: "800px",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <Typography sx={{ fontWeight: "600", fontSize: "14px", color: "#000" }}>
                {previewFile.file.name}
              </Typography>
              <Button
                onClick={() => setPreviewFile(null)}
                sx={{ minWidth: "auto", padding: "6px", color: "#666" }}
              >
                <X size={20} />
              </Button>
            </Box>
            <Box sx={{ flex: 1, overflow: "auto", backgroundColor: "#F5F5F5" }}>
              {isPdf(previewFile.file) ? (
                <Box
                  component="iframe"
                  src={previewFile.url}
                  onError={() => console.error("Failed to load PDF:", previewFile.url)}
                  sx={{ width: "100%", height: isMobile ? "70vh" : "80vh", border: "none" }}
                />
              ) : (
                <Box
                  component="img"
                  src={previewFile.url}
                  alt={previewFile.file.name}
                  onError={() => console.error("Failed to load image:", previewFile.url)}
                  sx={{ width: "100%", height: "auto", display: "block" }}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default HotelDocumentPage;

